"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import type React from "react";
import { audioPreloader } from "@/lib/audio-preloader";

/** How long a mouse must rest on a play control before its audio is fetched. */
export const HOVER_INTENT_DELAY_MS = 200;

export interface IntentPreloadHandlers {
  onPointerEnter: (event: React.PointerEvent) => void;
  onPointerLeave: () => void;
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerCancel: (event: React.PointerEvent) => void;
}

/**
 * Pointer handlers for a play control that start buffering its sound only
 * when the visitor signals they are about to press it:
 *
 * - mouse: resting on the control for HOVER_INTENT_DELAY_MS;
 * - touch/pen: the press itself (pointerdown), cancelled again by
 *   pointercancel, which the browser fires when the touch becomes a scroll.
 *
 * A list that is only scrolled past fetches nothing. Playback picks the
 * buffered (or still-buffering) element up from audioPreloader, so an intent
 * fetch is never repeated by the play that follows it.
 */
export function useIntentPreload(
  soundId: string | null | undefined,
  audioUrl: string | null | undefined,
): IntentPreloadHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const warm = useCallback(() => {
    if (soundId && audioUrl) void audioPreloader.preload(soundId, audioUrl);
  }, [soundId, audioUrl]);

  return useMemo(
    () => ({
      onPointerEnter: (event: React.PointerEvent) => {
        if (event.pointerType !== "mouse") return;
        clearTimer();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          warm();
        }, HOVER_INTENT_DELAY_MS);
      },
      onPointerLeave: clearTimer,
      onPointerDown: (event: React.PointerEvent) => {
        if (event.pointerType === "mouse") return;
        warm();
      },
      onPointerCancel: (event: React.PointerEvent) => {
        if (event.pointerType === "mouse" || !soundId) return;
        if (!audioPreloader.isReady(soundId)) audioPreloader.cancel(soundId);
      },
    }),
    [clearTimer, warm, soundId],
  );
}
